import { Injectable } from '@angular/core';
import {ServerService} from "./ServerService";
import {HttpClient} from "@angular/common/http";
import { WhatsAppTelefone, WhatsAppIntegracao } from '../cloud-whatsapp/config-whatsapp/whatsapp.interface';

export interface CloudWhatsappSdkConfig {
  appId: string;
  configId: string;
  version: string;
  redirectUri: string;
}

export interface ResultadoDiagnosticoCloudWhatsapp {
  tokenValido: boolean;
  numeroValido: boolean;
  webhookConfigurado: boolean;
  templatesSincronizados: boolean;
  detalhes: {
    empresaId: number;
    telefoneCloudWhatsappId?: number;
    whatsappId?: string;
    wabaId?: string;
    telefone?: string;
    verifyTokenConfigurado: boolean;
    quantidadeTemplatesLocais: number;
    quantidadeTemplatesMeta: number;
    quantidadeTemplatesEsperados?: number;
    quantidadeTemplatesMetaTotal?: number;
    appIdConfigurado?: string;
    tokenAppId?: string;
    tokenEscopos?: string[];
    nomeVerificadoNumero?: string;
    ratingNumero?: string;
    statusVerificacaoNumero?: string;
    webhookAssinado?: boolean;
    quantidadeAppsAssinados?: number;
    appsAssinadosIds?: string[];
    templatesEsperados?: string[];
    templatesFaltandoNaMeta?: string[];
    templatesFaltandoNoBanco?: string[];
    templatesDivergentes?: string[];
    templatesNaoAprovados?: string[];
    ultimoErro?: string;
  };
  warnings: string[];
}

@Injectable({
  providedIn: 'root'
})
export class CloudWhatsappService extends ServerService {
  constructor(protected http: HttpClient) {
    super(http);
  }

  obtenhaConfiguracaoSdk(): Promise<CloudWhatsappSdkConfig> {
    console.log('[CloudWhatsapp] Obtendo configuração do SDK do Facebook');

    return this.obtenha('/cloudwhatsapp/sdk-config', {})
      .then((response: CloudWhatsappSdkConfig) => {
        console.log('[CloudWhatsapp] Configuração do SDK recebida:', JSON.stringify(response));
        return response;
      })
      .catch(error => {
        console.error('[CloudWhatsapp] Erro ao obter configuração do SDK:', error);
        throw error;
      });
  }

  exchangeCodeForToken(authorizationCode: string, redirectUri?: string): Promise<string> {
    const dados = {
      code: authorizationCode,
      redirectUri
    };

    return this.facaPost('/whatsapp/exchange-token', dados)
      .then(response => {
        // Log detalhado para debug
        console.log('[CloudWhatsapp] Estrutura da resposta de token:',
          JSON.stringify({
            temSuccess: !!response?.success,
            temSucesso: !!response?.sucesso,
            temObjeto: !!response?.objeto,
            temData: !!response?.data,
            formato: typeof response,
            chaves: response ? Object.keys(response) : [],
            resposta: response
          })
        );

        // Verificar estrutura da resposta (pode ser diferente dependendo do formato da API)
        let accessToken = null;

        if (response && response.success && response.access_token) {
          // Formato direto da resposta
          accessToken = response.access_token;
        } else if (response && response.objeto && response.objeto.access_token) {
          // Formato com objeto wrapper
          accessToken = response.objeto.access_token;
        } else if (response && response.sucesso && response.data && response.data.access_token) {
          // Formato com wrapper do Resposta
          accessToken = response.data.access_token;
        } else if (response && response.sucesso && response.objeto && response.objeto.access_token) {
          // Formato combinado sucesso+objeto
          accessToken = response.objeto.access_token;
        } else if (response && typeof response === 'object' && response.access_token) {
          // Resposta direta (sem wrappers)
          accessToken = response.access_token;
        }

        if (!accessToken) {
          console.error('[CloudWhatsapp] Falha ao extrair access_token da resposta:', response);
          // Tentativa de extrair o token diretamente como string (último recurso)
          if (typeof response === 'string' && response.length > 20) {
            console.log('[CloudWhatsapp] Tentando interpretar resposta como token string direto');
            return response;
          }
          throw new Error('Falha ao obter access token: formato de resposta inválido');
        }

        return accessToken;
      })
      .catch(error => {
        console.error('[CloudWhatsapp] Erro ao trocar código por token:', error);
        throw error;
      });
  }

  obtenhaTelefones(accessToken: string): Promise<WhatsAppTelefone[]> {
    console.log('[CloudWhatsapp] Obtendo telefones com token:', accessToken ? accessToken.substring(0, 10) + '...' : 'vazio');

    if (!accessToken) {
      console.error('[CloudWhatsapp] Token de acesso ausente na chamada obtenhaTelefones');
      return Promise.reject(new Error('Token de acesso é obrigatório'));
    }

    const dados = {
      at: accessToken
    };

    console.log('[CloudWhatsapp] Enviando requisição para obter telefones');

    return this.obtenha('/whatsapp/telefones', dados)
      .then(response => {
        console.log('[CloudWhatsapp] Resposta obtenhaTelefones:', JSON.stringify(response));
        return response;
      })
      .catch(error => {
        console.error('[CloudWhatsapp] Erro ao obter telefones:', error);
        throw error;
      });
  }

  associe(accessToken: string, waba: WhatsAppTelefone): Promise<any> {
    console.log('[CloudWhatsapp] Iniciando associação de WhatsApp');
    console.log('[CloudWhatsapp] Token presente:', !!accessToken);
    console.log('[CloudWhatsapp] Dados do WABA:', JSON.stringify(waba));

    if (!accessToken) {
      console.error('[CloudWhatsapp] Token de acesso ausente na chamada associe');
      return Promise.reject(new Error('Token de acesso é obrigatório'));
    }

    if (!waba || !waba.waba_id) {
      console.error('[CloudWhatsapp] Dados do WhatsApp Business inválidos');
      return Promise.reject(new Error('Dados do WhatsApp Business são obrigatórios'));
    }

    const dados = {
      at: accessToken,
      waba: waba
    };

    console.log('[CloudWhatsapp] Enviando requisição para associar número', JSON.stringify(dados));

    return this.facaPost('/whatsapp/associe', dados)
      .then(response => {
        console.log('[CloudWhatsapp] Resposta da associação:', JSON.stringify(response));
        return response;
      })
      .catch(error => {
        console.error('[CloudWhatsapp] Erro ao associar WhatsApp:', error);
        throw error;
      });
  }

  desassocie(): Promise<any> {
    console.log('[CloudWhatsapp] Iniciando desassociação de WhatsApp');

    return this.facaPost('/whatsapp/desassocie', {})
      .then(response => {
        console.log('[CloudWhatsapp] Resposta da desassociação:', JSON.stringify(response));
        return response;
      })
      .catch(error => {
        console.error('[CloudWhatsapp] Erro ao desassociar WhatsApp:', error);
        throw error;
      });
  }

  testarConexao(): Promise<ResultadoDiagnosticoCloudWhatsapp> {
    console.log('[CloudWhatsapp] Executando diagnóstico real da conexão WhatsApp Cloud');

    return this.facaPost('/whatsapp/testar-conexao', {})
      .then(response => {
        console.log('[CloudWhatsapp] Resultado do diagnóstico:', JSON.stringify(response));
        return response;
      })
      .catch(error => {
        console.error('[CloudWhatsapp] Erro ao testar conexão:', error);
        throw error;
      });
  }

  salvarTokenTemporario(token: string): Promise<any> {
    console.log('[CloudWhatsapp][Dev] Salvando token temporário');

    return this.facaPost('/whatsapp/token-dev', { token })
      .then(response => {
        console.log('[CloudWhatsapp][Dev] Resposta salvarTokenTemporario:', JSON.stringify(response));
        return response;
      })
      .catch(error => {
        console.error('[CloudWhatsapp][Dev] Erro ao salvar token temporário:', error);
        throw error;
      });
  }

  salvarTokenTemporarioFixo(token: string): Promise<any> {
    console.log('[CloudWhatsapp][Dev] Salvando token temporário fixo');

    return this.facaPost('/whatsapp/token-dev-fixo', { token })
      .then(response => {
        console.log('[CloudWhatsapp][Dev] Resposta salvarTokenTemporarioFixo:', JSON.stringify(response));
        return response;
      })
      .catch(error => {
        console.error('[CloudWhatsapp][Dev] Erro ao salvar token temporário fixo:', error);
        throw error;
      });
  }

  carregueDados(): Promise<WhatsAppIntegracao> {
    console.log('[CloudWhatsapp] Carregando dados da integração');

    return this.obtenha('/whatsapp/integracao', {})
      .then(response => {
        console.log('[CloudWhatsapp] Dados de integração carregados:', JSON.stringify(response));
        return response;
      })
      .catch(error => {
        console.error('[CloudWhatsapp] Erro ao carregar dados da integração:', error);
        throw error;
      });
  }

  obtenhaResumoConversas(periodo: string = '30dias'): Promise<any> {
    console.log('[CloudWhatsapp] Obtendo resumo de conversas para período:', periodo);

    // Converter período para datas
    const dataFim = new Date();
    const dataInicio = new Date();

    switch (periodo) {
      case '7dias':
        dataInicio.setDate(dataFim.getDate() - 6);
        break;
      case '30dias':
        dataInicio.setDate(dataFim.getDate() - 29);
        break;
      case '90dias':
        dataInicio.setDate(dataFim.getDate() - 89);
        break;
      default:
        dataInicio.setDate(dataFim.getDate() - 29);
    }

    const formatarData = (data: Date) => {
      const ano = data.getFullYear();
      const mes = String(data.getMonth() + 1).padStart(2, '0');
      const dia = String(data.getDate()).padStart(2, '0');
      return `${ano}-${mes}-${dia}`;
    };

    const params = {
      dataInicio: formatarData(dataInicio),
      dataFim: formatarData(dataFim),
      periodoDescricao: periodo
    };

    console.log('[CloudWhatsapp] Parâmetros de data:', params);

    return this.obtenha('/whatsapp/resumo-conversas', params)
      .then(response => {
        console.log('[CloudWhatsapp] Resumo de conversas recebido');
        return response;
      })
      .catch(error => {
        console.error('[CloudWhatsapp] Erro ao obter resumo de conversas:', error);
        throw error;
      });
  }

  // ========== Métodos para gerenciamento de templates ==========

  obtenhaStatusTemplates(): Promise<{
    configurado: boolean;
    modo: 'unica' | 'por_status' | null;
    enviarTemplateForaJanela: boolean;
    enviarTemplateMarketing: boolean;
    templates: Array<{
      nome: string;
      status: 'APPROVED' | 'PENDING' | 'REJECTED' | 'NOT_CREATED';
      mensagem?: string;
    }>;
  }> {
    console.log('[CloudWhatsapp] Obtendo status dos templates');

    return this.obtenha('/whatsapp/templates/status', {})
      .then(response => {
        console.log('[CloudWhatsapp] Status dos templates recebido:', JSON.stringify(response));
        return response;
      })
      .catch(error => {
        console.error('[CloudWhatsapp] Erro ao obter status dos templates:', error);
        throw error;
      });
  }

  atualizeEnvioTemplateForaJanela(ativo: boolean): Promise<{ ativo: boolean }> {
    console.log('[CloudWhatsapp] Atualizando envio de template fora da janela:', ativo);

    return this.facaPost('/whatsapp/templates/envio-fora-janela', { ativo })
      .then(response => {
        console.log('[CloudWhatsapp] Resposta da atualizacao de envio fora da janela:', JSON.stringify(response));
        return response;
      })
      .catch(error => {
        console.error('[CloudWhatsapp] Erro ao atualizar envio fora da janela:', error);
        throw error;
      });
  }

  atualizeEnvioTemplateMarketing(ativo: boolean): Promise<{ ativo: boolean }> {
    console.log('[CloudWhatsapp] Atualizando envio de template de marketing:', ativo);

    return this.facaPost('/whatsapp/templates/envio-marketing', { ativo })
      .then(response => {
        console.log('[CloudWhatsapp] Resposta da atualizacao de envio marketing:', JSON.stringify(response));
        return response;
      })
      .catch(error => {
        console.error('[CloudWhatsapp] Erro ao atualizar envio marketing:', error);
        throw error;
      });
  }

  crieTemplatesNotificacao(): Promise<{ sucesso: boolean; mensagem?: string }> {
    console.log('[CloudWhatsapp] Criando templates de notificação');

    return this.facaPost('/whatsapp/templates/criar', {})
      .then(response => {
        console.log('[CloudWhatsapp] Resposta da criação de templates:', JSON.stringify(response));
        return response;
      })
      .catch(error => {
        console.error('[CloudWhatsapp] Erro ao criar templates:', error);
        throw error;
      });
  }

  sincronizeStatusTemplates(): Promise<any> {
    console.log('[CloudWhatsapp] Sincronizando status dos templates com Meta');

    return this.facaPost('/whatsapp/templates/sincronizar', {})
      .then(response => {
        console.log('[CloudWhatsapp] Resposta da sincronização:', JSON.stringify(response));
        return response;
      })
      .catch(error => {
        console.error('[CloudWhatsapp] Erro ao sincronizar templates:', error);
        throw error;
      });
  }

  sincronizeTemplatesDoFacebook(): Promise<{ sucesso: boolean; mensagem?: string; total?: number }> {
    console.log('[CloudWhatsapp] Buscando templates do Facebook e sincronizando com banco');

    return this.facaPost('/whatsapp-templates/sincronizar-do-facebook', {})
      .then(response => {
        console.log('[CloudWhatsapp] Resposta da sincronização do Facebook:', JSON.stringify(response));
        return response;
      })
      .catch(error => {
        console.error('[CloudWhatsapp] Erro ao sincronizar templates do Facebook:', error);
        throw error;
      });
  }

  envieTesteTemplate(templateId: number, telefoneDestino: string): Promise<{ mensagem: string }> {
    console.log('[CloudWhatsapp] Enviando teste de template', { templateId, telefoneDestino });

    return this.facaPost('/whatsapp-templates/enviar-teste', {
      templateId,
      telefoneDestino
    })
      .then(response => {
        console.log('[CloudWhatsapp] Resposta do envio de teste:', JSON.stringify(response));
        return response;
      })
      .catch(error => {
        console.error('[CloudWhatsapp] Erro ao enviar teste de template:', error);
        throw error;
      });
  }

  /**
   * Atualiza um template na API da Meta e no banco de dados
   * @param templateId ID do template no banco
   * @param dados Novos dados do template
   * @returns Resultado da atualização com sucesso/erro e mensagem da Meta
   */
  atualizeTemplate(
    templateId: number,
    dados: {
      headerText?: string;
      bodyText: string;
      footerText?: string;
      buttons?: Array<{
        actionType: string;
        text: string;
        value?: string;
        urlType?: string;
      }>;
    }
  ): Promise<{ sucesso: boolean; mensagem: string; erroMeta?: any }> {
    console.log('[CloudWhatsapp] Atualizando template na Meta', { templateId });

    return this.facaPost(`/whatsapp-templates/${templateId}/atualizar`, dados)
      .then(response => {
        console.log('[CloudWhatsapp] Resposta da atualização:', JSON.stringify(response));

        // Verificar se a resposta tem o formato esperado
        if (response && response.sucesso !== undefined) {
          return response;
        }

        // Formato com wrapper do Resposta
        if (response && response.objeto) {
          return response.objeto;
        }

        return response;
      })
      .catch(error => {
        console.error('[CloudWhatsapp] Erro ao atualizar template:', error);
        throw error;
      });
  }

  /**
   * Busca um template pelo ID
   * @param id ID do template no banco
   * @returns Dados do template no formato esperado pelo editor
   */
  obtenhaTemplatePorId(id: number): Promise<{
    id: number;
    nomeTemplate: string;
    tipoNotificacao: string;
    status: string;
    categoria: string;
    headerText: string;
    bodyText: string;
    footerText: string;
    buttonCategory: string;
    buttons: any[];
  }> {
    console.log('[CloudWhatsapp] Buscando template por ID:', id);

    return this.obtenha(`/whatsapp-templates/${id}`, {})
      .then(response => {
        console.log('[CloudWhatsapp] Template recebido:', JSON.stringify(response));
        return response;
      })
      .catch(error => {
        console.error('[CloudWhatsapp] Erro ao buscar template:', error);
        throw error;
      });
  }
}
